import { Component, NgZone, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AiSearchBarComponent, ChatDialogComponent, FileError, FileTransferService, UserService } from 'shared';
import { SuggestionBadgeComponent } from 'shared';
import { HomepageRecommendationCardComponent, HomepageRecommendation } from 'shared';
import { WorkspaceCardComponent, Workspace } from 'shared';
import { HomepageHeaderComponent, HomepageUser } from 'shared';
import { HomepageFooterComponent } from 'shared';
import { environment } from 'shared';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    AiSearchBarComponent,
    ChatDialogComponent,
    SuggestionBadgeComponent,
    HomepageRecommendationCardComponent,
    WorkspaceCardComponent,
    HomepageHeaderComponent,
    HomepageFooterComponent
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild(ChatDialogComponent) chatDialog!: ChatDialogComponent;
  basePath: string = environment.basePath;

  // Profile-specific content configuration
  // Each profile has its own recommendations (by period), workspaces, and suggestions
  profileData: {
    [profileName: string]: {
      profile: HomepageUser;
      suggestions: string[];
      recommendations: {
        daily: HomepageRecommendation[];
        weekly: HomepageRecommendation[];
        monthly: HomepageRecommendation[];
      };
      workspaces: Workspace[];
    };
  } = {
      claire: {
        profile: {
          name: 'Claire',
          role: 'Sales Representative',
          avatar: 'user-avatars/personas/claire.png'
        },
        suggestions: [
          'Onboard customer',
          'Add customer order',
          'Review sales commission',
          'Submit expense',
          'Amend contract',
          'Add new material',
          'Fulfill order',
          '•••'
        ],
        recommendations: {
          daily: [
            {
              id: 'c-d-1',
              title: 'Client Follow Up Required',
              description: 'You have 3 clients needing follow ups today. Stay on top of client relationships.',
              priority: 'high',
              actionLabel: 'Schedule calls',
              actionUrl: '/customer-management/'
            },
            {
              id: 'c-d-2',
              title: 'Resolve Query',
              description: 'Credit and Master Data Team has query on customer form submitted.',
              priority: 'medium',
              actionLabel: 'Answer query'
            },
            {
              id: 'c-d-3',
              title: 'Contract Amendment',
              description: '5 customers have requested for contract amendment. ',
              priority: 'low',
              actionLabel: 'View contract'
            }
          ],
          weekly: [
            {
              id: 'c-w-1',
              title: 'Material Master',
              description: 'Review new material details received from all department and add it in SAP.',
              priority: 'low',
              actionLabel: 'Add material'
            },
            {
              id: 'c-w-2',
              title: 'Escalate Onboarding',
              description: 'Nudge department to expedite customer onboarding for request pending more than a week.',
              priority: 'high',
              actionLabel: 'Send nudge'
            },
            {
              id: 'c-w-3',
              title: 'Sales Invoice',
              description: 'Review added sales invoice for weekly sales to facilitate commission calculation.',
              priority: 'medium',
              actionLabel: 'Add invoice'
            }
          ],
          monthly: [
            {
              id: 'c-m-1',
              title: 'Customer Feedback',
              description: 'Review customer onboarding feedback, satisfaction and average rating.',
              priority: 'medium',
              actionLabel: 'View report'
            },
            {
              id: 'c-m-2',
              title: 'Sales Commission',
              description: 'View details of payable monthly sales commission. You can raise questions if you have any.',
              priority: 'high',
              actionLabel: 'View commission'
            },
            {
              id: 'c-m-3',
              title: 'Master Data',
              description: 'Review and clean master data by removing/merging duplicate.',
              priority: 'low',
              actionLabel: 'Review master data'
            }
          ]
        },
        workspaces: [
          {
            id: 'employee',
            name: 'Employee',
            description: 'Employee self-service workspace',
            tooltip: 'Access self-service features for time-off requests, expense submissions, and personal information updates.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/briefcase.svg">'
          },
          {
            id: 'customer',
            name: 'Customer',
            description: 'Customer management workspace',
            tooltip: 'Manage customer relationships, track interactions, and handle account information for improved service delivery.',
            url: '/customer-management/',
            icon: '<img width="28" height="28" src="icons/users-group.svg">'
          },
          {
            id: 'sales',
            name: 'Sales',
            description: 'Sales representative workspace',
            tooltip: 'Track inventory levels, manage stock movements, and monitor product availability across locations.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/money-withdrawal.svg">'
          },
          {
            id: 'contracts',
            name: 'Contracts',
            description: 'Contract agent workspace',
            tooltip: 'Handles contract management, onboarding, and compliance to ensure contracts meet organizational standards.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/invoice.svg">'
          }
        ]
      },
      jason: {
        profile: {
          name: 'Jason',
          role: 'Inventory Manager',
          avatar: 'user-avatars/personas/jason-inventory-manager.png'
        },
        suggestions: [
          'Stockout risk',
          'Place rush order',
          'Track order',
          'View pending orders',
          'Review safety stock',
          'Perform what-if analysis',
          '•••'
        ],
        recommendations: {
          daily: [
            {
              id: 'j-d-1',
              title: 'Aging Inventory Analysis',
              description: 'You have 5 SKUs below safety threshold. Resolve issue to optimize inventory.',
              priority: 'high',
              actionLabel: 'View analysis',
              actionUrl: '/inventory-manager/'
            },
            {
              id: 'j-d-2',
              title: 'Rush Order',
              description: 'Place rush order to manage growing demand of a product on time.',
              priority: 'medium',
              actionLabel: 'Place order'
            },
            {
              id: 'j-d-3',
              title: 'Live Order Updates',
              description: 'Track live order to ensure inventory delivery is on time for efficient inventory management.',
              priority: 'low',
              actionLabel: 'Review details'
            }
          ],
          weekly: [
            {
              id: 'j-w-1',
              title: 'Cost Optimization',
              description: 'Reduce safety stocks for stable item to save money and inventory space.',
              priority: 'low',
              actionLabel: 'Review details'
            },
            {
              id: 'j-w-2',
              title: 'Inventory Space Management',
              description: 'Manage space of planned upcoming inventory delivery',
              priority: 'high',
              actionLabel: 'Review details',
              actionUrl: '/inventory-manager/'
            },
            {
              id: 'j-w-3',
              title: 'Stock Replenishment Order',
              description: 'Review regular stock replenishment order and make any adjustment as per the requirement',
              priority: 'medium',
              actionLabel: 'Review details',
              actionUrl: '/inventory-manager/'
            }
          ],
          monthly: [
            {
              id: 'j-m-1',
              title: 'Seasonal Preparation',
              description: 'Increase buffer for holiday season to avoid stockout risk and maintain inventory flow. ',
              priority: 'medium',
              actionLabel: 'Prepare order'
            },
            {
              id: 'j-m-2',
              title: 'Inventory Performance Analysis',
              description: 'Review inventory turnover, stock aging, and dead stock percentage ',
              priority: 'high',
              actionLabel: 'View report'
            },
            {
              id: 'j-m-3',
              title: 'Forecast and Planning',
              description: 'Use historical data and predictive analytics to forecast demand for the next month or season.',
              priority: 'low',
              actionLabel: 'Prepare order'
            }
          ]
        },
        workspaces: [
          {
            id: 'employee',
            name: 'Employee',
            description: 'Employee self-service workspace',
            tooltip: 'Access self-service features for time-off requests, expense submissions, and personal information updates.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/briefcase.svg">'
          },
          {
            id: 'inventory',
            name: 'Inventory',
            description: 'Inventory management workspace',
            tooltip: 'Track inventory levels, manage stock movements, and monitor product availability across locations.',
            url: '/inventory-manager/',
            icon: '<img width="28" height="28" src="icons/invoice.svg">'
          },
          {
            id: 'warehouse',
            name: 'Warehouse',
            description: 'Warehouse operations workspace',
            tooltip: 'Manage warehouse operations and maintenance schedules.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/users-group.svg">'
          },
          {
            id: 'logistics',
            name: 'Logistics',
            description: 'Logistics and shipping workspace',
            tooltip: 'Manage logistics operations, shipping, receiving, and delivery optimization.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/money-withdrawal.svg">'
          }
        ]
      },
      john: {
        profile: {
          name: 'John',
          role: 'Purchasing Agent',
          avatar: 'user-avatars/personas/john.png'
        },
        suggestions: [
          'Request New Vendor',
          'Review new vendor requests',
          'Approve onboarding packet',
          'Chase missing vendor docs',
          'Check vendor risk',
          'Review pending profile creation',
          'Monitor vendor turnaround',
          '•••'
        ],
        recommendations: {
          daily: [
            {
              id: 'j-d-1',
              title: 'New Vendor Requests Need Review',
              description: 'You have 4 vendor requests waiting for manager review. Prioritize the oldest submissions to keep onboarding moving.',
              priority: 'high',
              actionLabel: 'Review requests',
              actionUrl: '/vendor-onboarding/'
            },
            {
              id: 'j-d-2',
              title: 'Missing Compliance Documents',
              description: 'Two vendors are still missing tax or registration documents needed before profile creation can continue.',
              priority: 'medium',
              actionLabel: 'Follow up',
              actionUrl: '/vendor-onboarding/'
            },
            {
              id: 'j-d-3',
              title: 'Duplicate Vendor Check',
              description: 'A newly submitted request appears to match an existing vendor. Resolve the duplicate before approval.',
              priority: 'low',
              actionLabel: 'Open vendors',
              actionUrl: '/vendor-onboarding/'
            }
          ],
          weekly: [
            {
              id: 'j-w-1',
              title: 'Pending Profile Creation',
              description: 'Three approved vendors are waiting for final profile setup. Completing them this week will reduce onboarding backlog.',
              priority: 'low',
              actionLabel: 'Create profiles',
              actionUrl: '/vendor-onboarding/'
            },
            {
              id: 'j-w-2',
              title: 'Vendor Follow-Up Queue',
              description: 'Several vendors have not responded to requests for missing onboarding details. Send reminders to keep approvals on schedule.',
              priority: 'high',
              actionLabel: 'Send reminder',
              actionUrl: '/vendor-onboarding/'
            },
            {
              id: 'j-w-3',
              title: 'High-Risk Vendors to Review',
              description: 'Review onboarding records flagged for banking or tax validation so they do not stall in the queue.',
              priority: 'medium',
              actionLabel: 'Review risk',
              actionUrl: '/vendor-onboarding/'
            }
          ],
          monthly: [
            {
              id: 'j-m-1',
              title: 'Onboarding Turnaround Trends',
              description: 'Review monthly cycle time for vendor onboarding and identify where approvals or document collection are slowing down.',
              priority: 'medium',
              actionLabel: 'View trends',
              actionUrl: '/vendor-onboarding/dashboard?profile=john'
            },
            {
              id: 'j-m-2',
              title: 'Vendor Portfolio Health',
              description: 'Audit active and newly onboarded vendors for duplicate records, incomplete tax data, and outdated contact details.',
              priority: 'high',
              actionLabel: 'Open dashboard',
              actionUrl: '/vendor-onboarding/dashboard?profile=john'
            },
            {
              id: 'j-m-3',
              title: 'Manager Capacity Planning',
              description: 'Use request volume trends to plan vendor manager coverage for the next month and avoid approval bottlenecks.',
              priority: 'low',
              actionLabel: 'Plan capacity',
              actionUrl: '/vendor-onboarding/dashboard?profile=john'
            }
          ]
        },
        workspaces: [
          {
            id: 'employee',
            name: 'Employee',
            description: 'Employee self-service workspace',
            tooltip: 'Access self-service features for time-off requests, expense submissions, and personal information updates.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/briefcase.svg">'
          },
          {
            id: 'vendors',
            name: 'Vendors',
            description: 'Vendor onboarding workspace',
            tooltip: 'Review vendor requests, manage onboarding progress, and resolve compliance issues before profiles are approved.',
            url: '/vendor-onboarding/',
            icon: '<img width="28" height="28" src="icons/users-group.svg">'
          },
          {
            id: 'warehouse',
            name: 'Warehouse',
            description: 'Warehouse operations workspace',
            tooltip: 'Manage warehouse operations and maintenance schedules.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/users-group.svg">'
          },
          {
            id: 'logistics',
            name: 'Logistics',
            description: 'Logistics and shipping workspace',
            tooltip: 'Manage logistics operations, shipping, receiving, and delivery optimization.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/money-withdrawal.svg">'
          }
        ]
      },
      aaron: {
        profile: {
          name: 'Aaron',
          role: 'Employee',
          avatar: 'user-avatars/personas/aaron.png'
        },
        suggestions: [
          'Submit expense',
          'View reimbursements',
          'Track request status',
          'Upload receipt',
          'View payment details',
          'Check policy',
          '•••'
        ],
        recommendations: {
          daily: [
            {
              id: 'a-d-1',
              title: 'Pending Expense Approval',
              description: 'You have 2 expense requests pending manager approval. Track their status.',
              priority: 'high',
              actionLabel: 'View status',
              actionUrl: '/expense-reimbursement/'
            },
            {
              id: 'a-d-2',
              title: 'Receipt Upload',
              description: 'Upload receipts for recent business expenses before the submission deadline.',
              priority: 'medium',
              actionLabel: 'Upload receipt'
            },
            {
              id: 'a-d-3',
              title: 'Policy Update',
              description: 'Review updated expense reimbursement policy for travel expenses.',
              priority: 'low',
              actionLabel: 'View policy'
            }
          ],
          weekly: [
            {
              id: 'a-w-1',
              title: 'Expense Summary',
              description: 'Review your weekly expense summary and ensure all receipts are submitted.',
              priority: 'low',
              actionLabel: 'View summary'
            },
            {
              id: 'a-w-2',
              title: 'Reimbursement Status',
              description: 'Check the status of your pending reimbursements.',
              priority: 'high',
              actionLabel: 'Track status',
              actionUrl: '/expense-reimbursement/'
            },
            {
              id: 'a-w-3',
              title: 'Missing Receipts',
              description: 'You have 3 expenses missing receipts. Upload them to avoid delays.',
              priority: 'medium',
              actionLabel: 'Upload receipts'
            }
          ],
          monthly: [
            {
              id: 'a-m-1',
              title: 'Monthly Expense Report',
              description: 'Review your monthly expense report and categorization.',
              priority: 'medium',
              actionLabel: 'View report'
            },
            {
              id: 'a-m-2',
              title: 'Payment History',
              description: 'View your reimbursement payment history for the month.',
              priority: 'high',
              actionLabel: 'View payments'
            },
            {
              id: 'a-m-3',
              title: 'Budget Review',
              description: 'Review your expense budget utilization for the current period.',
              priority: 'low',
              actionLabel: 'View budget'
            }
          ]
        },
        workspaces: [
          {
            id: 'employee',
            name: 'Employee',
            description: 'Employee self-service workspace',
            tooltip: 'Access self-service features for time-off requests, expense submissions, and personal information updates.',
            url: '/workspace/',
            icon: '<img width="28" height="28" src="icons/briefcase.svg">'
          },
          {
            id: 'expenses',
            name: 'Expenses',
            description: 'Expense reimbursement workspace',
            tooltip: 'Submit expense requests, upload receipts, and track reimbursement status.',
            url: '/expense-reimbursement/',
            icon: '<img width="28" height="28" src="icons/money-withdrawal.svg">'
          },
          {
            id: 'inventory',
            name: 'Inventory',
            description: 'Inventory management workspace',
            tooltip: 'Track inventory levels, manage stock movements, and monitor product availability across locations.',
            url: '/inventory-manager/',
            icon: '<img width="28" height="28" src="icons/invoice.svg">'
          },
          {
            id: 'vendor',
            name: 'Vendor',
            description: 'Vendor onboarding workspace',
            tooltip: 'Manage vendor relationships and onboarding processes.',
            url: '/vendor-onboarding/',
            icon: '<img width="28" height="28" src="icons/users-group.svg">'
          }
        ]
      }
    };

  // Authenticated user from ServiceNow (if embedded)
  private authenticatedUser?: HomepageUser;

  // Get profiles array from profileData, plus authenticated user if available
  get profiles(): HomepageUser[] {
    const mockProfiles = Object.values(this.profileData).map(p => p.profile);
    // Add authenticated user at the beginning if available and not already a mock profile
    if (this.authenticatedUser) {
      const isAlreadyMock = mockProfiles.some(
        p => p.name.toLowerCase() === this.authenticatedUser!.name.toLowerCase()
      );
      if (!isAlreadyMock) {
        return [this.authenticatedUser, ...mockProfiles];
      }
    }
    return mockProfiles;
  }

  // Current active user (defaults to first profile)
  user: HomepageUser = this.profileData['claire'].profile;

  // ServiceNow username for content lookup (more reliable than display name)
  private snUserName?: string;
  // Original ServiceNow username (preserved for switching back to authenticated user)
  private originalSnUserName?: string;

  // Get the profile key for content lookup (falls back to 'claire' for unknown users)
  private get contentProfileKey(): string {
    // Use ServiceNow username if available, otherwise fall back to display name
    const profileKey = this.snUserName || this.user.name.toLowerCase();
    // Check if profile exists (e.g., 'demo.claire' -> 'claire' mapping)
    const normalizedKey = profileKey.replace('demo.', '');
    return this.profileData[normalizedKey] ? normalizedKey : 'claire';
  }

  // Get recommendations for current user and selected period
  get recommendations(): HomepageRecommendation[] {
    const data = this.profileData[this.contentProfileKey];
    return data?.recommendations[this.selectedPeriod] || [];
  }

  // Get workspaces for current user
  get workspaces(): Workspace[] {
    const data = this.profileData[this.contentProfileKey];
    return data?.workspaces || [];
  }

  // Get suggestions for current user
  get suggestions(): string[] {
    const data = this.profileData[this.contentProfileKey];
    return data?.suggestions || [];
  }

  searchValue: string = '';
  attachedFiles: File[] = [];
  selectedPeriod: 'daily' | 'weekly' | 'monthly' = 'daily';

  isRecording = false;

  // Web Speech API for live transcription
  private recognition: any = null;
  private isSupported = false;
  private userSubscription?: Subscription;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private fileTransfer: FileTransferService,
    private route: ActivatedRoute,
    private userService: UserService
  ) {
    this.initSpeechRecognition();
  }

  ngOnInit(): void {
    // Subscribe to user context from ServiceNow host bridge
    this.userSubscription = this.userService.user$.subscribe(userProfile => {
      if (userProfile && this.userService.isEmbedded()) {
        // Store ServiceNow username for content lookup (and preserve original for switching back)
        this.snUserName = userProfile.sn_user_name;
        this.originalSnUserName = userProfile.sn_user_name;
        // Create and store the authenticated user profile
        this.authenticatedUser = {
          name: userProfile.name,
          role: userProfile.title || userProfile.role,
          avatar: userProfile.avatar
        };
        // Set as current user
        this.user = this.authenticatedUser;
        console.log('[Profile] Loaded from ServiceNow host:', this.user.name, '(username:', this.snUserName, ')');
      }
    });

    // Check for profile query parameter in URL (e.g., /?profile=claire, /?profile=jason, /?profile=aaron)
    this.route.queryParams.subscribe(params => {
      const profileName = params['profile'];
      if (profileName) {
        const matchedProfile = this.profiles.find(
          p => p.name.toLowerCase() === profileName.toLowerCase()
        );
        if (matchedProfile) {
          this.user = matchedProfile;
          console.log('[Profile] Loaded from URL:', matchedProfile.name);
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.recognition) {
      this.recognition.stop();
    }
    this.userSubscription?.unsubscribe();
  }

  private initSpeechRecognition(): void {
    // Check if browser supports speech recognition
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (SpeechRecognitionAPI) {
      this.isSupported = true;
      this.recognition = new SpeechRecognitionAPI();
      this.recognition.continuous = true; // Keep listening continuously
      this.recognition.interimResults = true; // Get partial results as user speaks
      this.recognition.lang = 'en-US';

      this.recognition.onresult = (event: any) => {
        this.ngZone.run(() => {
          // Combine all results into a single transcript
          const transcript = Array.from(event.results)
            .map((result: any) => result[0])
            .map((result: any) => result.transcript)
            .join('');

          // Update the search value in real-time as user speaks
          this.searchValue = transcript;
          console.log('[Voice] Transcript:', transcript);

          // When user stops speaking (final result), stop recording and submit
          const lastResult = event.results[event.results.length - 1];
          if (lastResult.isFinal && transcript.trim()) {
            console.log('[Voice] Final transcript:', transcript);

            // Stop speech recognition
            this.recognition?.stop();
            this.isRecording = false;

            // Auto-submit the form
            setTimeout(() => {
              this.onSearch(transcript);
            }, 100);
          }
        });
      };

      this.recognition.onend = () => {
        this.ngZone.run(() => {
          console.log('[Voice] Speech recognition ended');
          this.isRecording = false;
        });
      };

      this.recognition.onerror = (event: any) => {
        this.ngZone.run(() => {
          console.error('[Voice] Speech recognition error:', event.error);
          this.isRecording = false;
        });
      };

      console.log('[Voice] Speech recognition initialized');
    } else {
      console.warn('[Voice] Speech recognition not supported in this browser');
    }
  }

  onSearch(value: string): void {
    if (value && value.trim()) {
      this.chatDialog.open(value);
      this.searchValue = ''; // Optional: Clear the home search bar after opening dialog
    }
  }

  async onSuggestionClick(suggestion: string): Promise<void> {
    // Check if this is "Onboard Customer" with attached files
    if (suggestion.toLowerCase() === 'onboard customer' && this.attachedFiles.length > 0) {
      console.log('[Files] Storing files for transfer:', this.attachedFiles.map(f => f.name));
      await this.fileTransfer.storeFiles(this.attachedFiles);
      // Navigate directly to customer-management add-new-customer page
      window.location.href = this.basePath + '/customer-management/#/add-new-customer';
      return;
    }

    // "Request New Vendor" goes straight to the vendor duplicate check, which is
    // the first step of the new vendor request flow in Vendor Onboarding.
    if (suggestion.toLowerCase() === 'request new vendor') {
      window.location.href = this.basePath + '/vendor-onboarding/vendor-check?profile=john';
      return;
    }

    this.searchValue = suggestion;
    this.onSearch(suggestion);
  }

  onRecommendationAction(recommendation: HomepageRecommendation): void {
    if (recommendation.actionUrl) {
      window.location.href = this.basePath + recommendation.actionUrl;
    }
  }

  onWorkspaceClick(workspace: Workspace): void {
    window.location.href = this.basePath + workspace.url;
  }

  selectPeriod(period: 'daily' | 'weekly' | 'monthly'): void {
    this.selectedPeriod = period;
  }

  onFilesChanged(files: File[]): void {
    this.attachedFiles = files;
    console.log('[Files] Attached files:', files.map(f => f.name));
  }

  onFileError(error: FileError): void {
    alert(`Error: ${error.reason}`);
    console.error('[Files] File error:', error.file.name, error.reason);
  }

  onVoiceInput(): void {
    if (!this.recognition) {
      console.warn('[Voice] Speech recognition not available');
      return;
    }

    if (this.isRecording) {
      // Stop listening
      console.log('[Voice] Stopping speech recognition');
      this.recognition.stop();
      this.isRecording = false;
    } else {
      // Start listening
      console.log('[Voice] Starting speech recognition');
      this.searchValue = ''; // Clear previous value
      try {
        this.recognition.start();
        this.isRecording = true;
      } catch (error) {
        console.error('[Voice] Error starting speech recognition:', error);
      }
    }
  }

  onProfileChange(profile: HomepageUser): void {
    console.log('[Profile] Switching to:', profile.name);
    this.user = profile;

    // Check if switching back to the authenticated user
    if (this.authenticatedUser && profile.name === this.authenticatedUser.name) {
      // Restore original ServiceNow username for content lookup
      this.snUserName = this.originalSnUserName;
    } else {
      // Clear ServiceNow username so contentProfileKey uses the selected profile name
      this.snUserName = undefined;
    }
  }
}
